<template>
  <q-layout
    view="hHh Lpr lFf"
    :class="{
      'document-notification-visible': unsignedDocument,
      'fullscreen-mode': isKioskActive,
    }"
  >
    <q-header v-if="!isKioskActive" elevated>
      <q-bar v-if="getConfig().INSTANCE_TYPE === 'staging'" dark class="bg-red text-white">
        <div class="col text-center text-weight-bold">
          {{ t('layout.instanceTypeWarning') }}
        </div>
      </q-bar>
      <q-bar v-if="isImpersonated" dark class="bg-orange text-white">
        <div class="col text-center text-weight-bold">
          {{ t('layout.impersonationWarning') }}
        </div>
      </q-bar>
      <div v-if="unsignedDocument">
        <document-notification-bar :unsigned-document="unsignedDocument" />
      </div>
      <q-toolbar>
        <c-page-section squares>
          <button
            type="button"
            class="menu-header toolbar-height"
            :class="{ 'menu-open': leftMenuShow }"
            :aria-label="leftMenuShow ? t('toolbar.menuClose') : t('toolbar.menuOpen')"
            @click="leftMenuShow = !leftMenuShow"
          >
            <template v-if="leftMenuShow">
              <q-icon color="input" size="md" name="menu_open" />
            </template>
            <template v-else>
              <q-icon color="input" size="md" name="menu" />
            </template>
          </button>
        </c-page-section>

        <c-page-section squares :row="false" css-class="margin-left width-percent-100" justify-content="space-between">
          <div v-if="displayToolbarIcon" class="toolbar-icon-section toolbar-height border-right">
            <router-link :to="{ name: PAGE_NAMES.DASHBOARD }">
              <q-avatar :size="`${toolbarIconSize}px`">
                <img src="~/assets/logo.png" alt="logo" />
              </q-avatar>
            </router-link>
          </div>

          <q-toolbar inset>
            <q-toolbar-title>
              <div class="text-logo-section">
                <span class="page-title ellipsis" data-testid="page-name">{{ t('menu.' + pageTitle) }}</span>
              </div>
            </q-toolbar-title>
          </q-toolbar>
          <div data-testid="current-user-login">
            <q-tooltip
              v-if="showingUserFullName === false"
              :delay="1000"
              class="toolbar-current-user-full-name"
              data-testid="current-user-full-name"
            >
              {{ userFullName }}
            </q-tooltip>

            <q-btn-dropdown v-model="showingUserFullName" content-class="box-shadow" :menu-offset="[1, 0]">
              <template #label>
                <div class="q-pl-md toolbar-user-section border-left toolbar-height flex no-wrap flex-center">
                  <user-avatar :full-name="userFullName" :size="`${toolbarUserPhotoSize}px`" :url="userAvatarUrl" />
                </div>
              </template>
              <q-list class="login-context-menu" bordered dense>
                <q-item tag="label">
                  <q-item-section side>
                    <q-toggle
                      v-model="darkMode"
                      :class="{
                        'q-pl-md': hasToggleLeftPadding,
                      }"
                      :color="getTenantColor()"
                      :icon="darkMode ? 'dark_mode' : 'light_mode'"
                      size="l"
                    />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ t('toolbar.darkMode') }}</q-item-label>
                  </q-item-section>
                </q-item>
                <q-separator />
                <q-item clickable data-testid="user-settings" :to="{ name: userSettingPage.name }">
                  <q-item-section side>
                    <q-icon name="mdi-account-cog q-pl-md q-mr-md" :color="getTenantColor()" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-section> {{ t(`menu.${userSettingPage.name}`) }}</q-item-section>
                  </q-item-section>
                </q-item>
                <q-separator />
                <q-item clickable data-testid="logout" @click="() => logout(true)">
                  <q-item-section side>
                    <q-icon name="mdi-logout q-pl-md q-mr-md" :color="getTenantColor()" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-section> {{ t('toolbar.logout') }}</q-item-section>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-btn-dropdown>
          </div>
        </c-page-section>
      </q-toolbar>
    </q-header>

    <q-drawer
      v-if="!isKioskActive"
      v-model="leftMenuShow"
      show-if-above
      :overlay="!leftMenuShow"
      :width="286"
      content-class="q-header"
    >
      <left-menu />
    </q-drawer>

    <q-page-container>
      <div
        class="page-content q-pb-md"
        :class="{
          'with-menu': leftMenuShow && !isKioskActive,
        }"
      >
        <router-view />
      </div>
    </q-page-container>
  </q-layout>
  <security-t-f-dialog ref="refSecurityTFDialog" />
</template>

<script lang="ts" setup>
import { ref, watch, computed, onMounted } from 'vue';
import LeftMenu from '@/layouts/LeftMenu.vue';
import CPageSection from '@/components/_common/PageSection.vue';
import { useRoute } from 'vue-router';
import { useTvMode } from '@/composables/useTvMode';
import { usePage } from '@/composables/usePage';
import { useI18n } from 'vue-i18n';

import { useDarkMode } from '@/composables/useDarkMode';
import { useWindowDimensions } from '@/composables/useWindowDimensions';
import { bp720, bp490 } from '@/constants/breakpoints';
import { PAGE_NAMES } from '@/constants/pages';
import SecurityTFDialog from '@/components/security/SecurityDialog.vue';
import { getConfig, getTenantColor } from '@/config';
import UserAvatar from '@/components/_common/UserAvatar.vue';
import DocumentNotificationBar from '@/components/documents/DocumentNotificationBar.vue';
import documentsModule from '@/store/modules/DocumentsModule';
import { hubClient } from '@tfpl/api-client';
import { useAuthStore } from '@/store/auth';
import { useAuthentication } from '@/composables/auth/useAuthentication';

/*****************************************************************************
 * SETUP
 ****************************************************************************/

const route = useRoute();
const { isKioskActive } = useTvMode();
const { getTranslateByName, pages } = usePage();
const { t } = useI18n();
const authStore = useAuthStore();
const isImpersonated = computed(() => authStore.isImpersonated);
const { setDarkMode, getDarkMode } = useDarkMode();
const windowDimensions = useWindowDimensions();
const refSecurityTFDialog = ref(SecurityTFDialog);
const MONTH_CHECK = 6;

// q-avatar sizes
const toolbarIconSize = ref(0);
const toolbarUserPhotoSize = ref(0);
const updateAvatarSizes = (newValue = windowDimensions) => {
  if (newValue.width < bp720) {
    toolbarIconSize.value = 50;
    toolbarUserPhotoSize.value = 40;
  } else {
    toolbarIconSize.value = 59;
    toolbarUserPhotoSize.value = 50;
  }
};
updateAvatarSizes();
watch(windowDimensions, updateAvatarSizes);

// general responsiveness
const displayToolbarIcon = computed(() => windowDimensions.width > bp490);
const hasToggleLeftPadding = computed(() => windowDimensions.width <= bp490);

//dark mode
const darkMode = ref(true);
darkMode.value = getDarkMode();
watch(darkMode, (newValue) => {
  setDarkMode(newValue);
});

// page title
const pageTitle = ref('');
pageTitle.value = getTranslateByName(route.name as string);
const setPageTitle = () => {
  pageTitle.value = getTranslateByName(route.name as string);
};
watch(() => route.name, setPageTitle);

// user info
const userFullName = computed(() => `${authStore.currentUser?.firstName} ${authStore.currentUser?.lastName}`);
const userAvatarUrl = computed(() => authStore.currentUser?.slackAvatarUrl);
const { logout } = useAuthentication();
const today = new Date(Date.now());
const showingUserFullName = ref(false);

const checkSecurityTF = () => {
  const confirmDate = new Date(authStore.currentUser ? authStore.currentUser.confirmSecurityTFDate : Date.now());
  confirmDate.setMonth(confirmDate.getMonth() + MONTH_CHECK);
  if ((refSecurityTFDialog.value && confirmDate <= today) || authStore.currentUser?.confirmSecurityTFDate == null) {
    refSecurityTFDialog.value.showDialog(true);
  }
};
onMounted(() => {
  checkSecurityTF();

  // document notification
  documentsModule.clearData();
  documentsModule.fetchDocuments({
    uuid: authStore.currentUser?.uuid as string,
    month: '',
  });
});

const documents = computed(() => documentsModule.documents);
const unsignedDocument = computed(() =>
  documents.value.find(
    (document) =>
      document.documentState === hubClient.DocumentState.WAITING_FOR_SIGN &&
      document.user.uuid === (authStore.currentUser?.uuid as string),
  ),
);

const userSettingPage = pages[PAGE_NAMES.USER_SETTINGS];

const leftMenuShow = ref(true);
</script>

<style lang="scss" scoped>
@import 'styles/app.variables.scss';
@import 'styles/quasar.variables';

.text-logo-section {
  height: 81px;
  display: flex;
  padding-top: 28px;
  padding-bottom: 30px;
}

.toolbar-height {
  height: $toolbar-height;

  @media screen and (max-width: $bp720) {
    height: $toolbar-height-small;
  }
}
.q-header {
  z-index: 4000;
}
.menu-header {
  width: $toolbar-height;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  position: relative;

  @media screen and (max-width: $bp720) {
    width: $toolbar-height-small;
  }
}
// Zero-specificity reset so the global .menu-header/.menu-open hover and
// active styles (src/styles/quasar/_special.scss) keep winning on the native <button>.
:where(button.menu-header) {
  padding: 0;
  border: 0;
  font: inherit;
  color: inherit;
  background: none;
}
.menu-header i {
  float: right;
  margin-top: 3px;
}

.toolbar-user-section {
  .name {
    font-size: $text-regular-size;
    white-space: nowrap;
  }
}

.toolbar-dark-mode-section {
  white-space: nowrap;
}
.body--light .toolbar-dark-mode-section {
  color: #000;
}
.body--dark .toolbar-dark-mode-section {
  color: #bbbbbb;
}

.toolbar-icon-section {
  width: $toolbar-height;
  margin-right: calc(#{$layout-padding-small} + #{$border-width});

  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media screen and (max-width: $bp720) {
    width: $toolbar-height-small;
  }

  .q-avatar {
    font-size: 59px;
    img {
      image-rendering: -webkit-optimize-contrast;
    }
  }
}

.toolbar-logout-section:deep(button > span.q-btn__content) {
  i {
    font-size: 10px;
  }
}

.page-content {
  padding-left: $layout-padding;

  @media screen and (max-width: $bp1200) {
    padding-left: $layout-padding-small;
  }

  &.with-menu {
    padding-left: calc((#{$layout-padding} - 4px) * 2);

    @media screen and (max-width: $bp1200) {
      padding-left: calc((#{$layout-padding-small} - 2px) * 2);
    }
  }
}

.fullscreen-mode {
  .page-content {
    padding-left: 0;
  }
  :deep(.padding-right) {
    padding-right: 0 !important;
  }
  :deep(.padding-top) {
    padding-top: 0 !important;
  }
  :deep(.colleague-list) {
    margin: map-get($space-sm, y) map-get($space-sm, x) !important;
  }
}

.page-title {
  margin-top: -6px;
  font-size: $heading-regular-size-small;

  @media screen and (max-width: $bp860) {
    margin-left: 0;
  }
  @media screen and (max-width: $bp720) {
    font-size: $heading-regular-size-extra-small;
    margin-top: -3px;
  }
  @media screen and (max-width: $bp490) {
    margin-left: $layout-padding-small;
  }
}
.toolbar-avatar {
  padding: 16px;
}

:deep(aside) {
  max-width: calc(100vw - ($layout-padding-small * 2));
  background-color: unset;
  margin-left: $layout-padding;
  margin-top: $layout-padding;

  z-index: 4000;

  @media screen and (max-width: $bp1200) {
    margin-left: $layout-padding-small;
    margin-top: $layout-padding-small;
  }
}

:global(body aside) {
  @media screen and (max-width: $bp1013) {
    margin-top: 110px !important;
    width: 100% !important;
  }
  @media screen and (max-width: $bp720) {
    margin-top: 96px !important;
    width: 100% !important;
  }
}
:global(body .document-notification-visible aside) {
  @media screen and (max-width: $bp1013) {
    margin-top: 184px !important;
    width: 100% !important;
  }
  @media screen and (max-width: $bp720) {
    margin-top: 190px !important;
    width: 100% !important;
  }
}
.login-context-menu {
  width: 250px;
}
:deep(button > span.q-btn__content) {
  i {
    margin: 0;
    padding: 0 6px 0 4px;
  }
}
</style>
